import { random, shuffled, neighbors, adjacent, slide, reject } from './53-game-primitives.mjs';

/** Randomized bounded Hamiltonian search gives an original solved route. A rotated
 * serpentine fallback guarantees termination without claiming unique puzzles. */
export function routedCells(state, columns) {
  const size = columns * columns;
  let visits = 0;
  function visit(path, used) {
    if (path.length === size) return path;
    if (++visits > 12000) return null;
    for (const next of shuffled(state, neighbors(path.at(-1), columns, size))) {
      if (used.has(next)) continue;
      used.add(next);
      path.push(next);
      const result = visit(path, used);
      if (result) return result;
      path.pop();
      used.delete(next);
    }
    return null;
  }
  const start = random(state, size);
  const found = visit([start], new Set([start]));
  if (found) return found;
  const snake = Array.from(
    { length: size },
    (_, i) =>
      Math.floor(i / columns) * columns +
      (Math.floor(i / columns) % 2 ? columns - 1 - (i % columns) : i % columns),
  );
  return random(state, 2) ? snake.reverse() : snake;
}

function ribbonRound(state, stage = 1) {
  const route = routedCells(state, 4);
  const count = stage === 1 ? 3 : 4;
  const lengths = Array(count).fill(3);
  for (let remaining = 16 - count * 3; remaining > 0; remaining--) lengths[random(state, count)]++;
  let offset = 0;
  const solution = lengths.map((length) => {
    const segment = route.slice(offset, offset + length);
    offset += length;
    return segment;
  });
  return {
    stage,
    board: Array(16).fill(-1),
    pairs: solution.map((p) => [p[0], p.at(-1)]),
    paths: Array.from({ length: count }, () => []),
    active: -1,
    steps: 0,
    combo: 0,
  };
}

function memoryRound(state, stage = 1, combo = 0) {
  const count = stage === 1 ? 6 : 8;
  return {
    stage,
    board: shuffled(
      state,
      Array.from({ length: count * 2 }, (_, i) => Math.floor(i / 2)),
    ),
    opened: [],
    found: [],
    combo,
    attempts: 0,
    events: [],
  };
}

function slideRound(state, stage = 1) {
  const board = [1, 2, 3, 4, 5, 6, 7, 8, 0];
  const scramble = stage === 1 ? 6 : stage === 2 ? 12 : 20;
  let previous = -1;
  for (let step = 0; step < scramble; step++) {
    const blank = board.indexOf(0),
      choices = neighbors(blank, 3, 9).filter((i) => i !== previous),
      next = choices[random(state, choices.length)];
    [board[blank], board[next]] = [board[next], board[blank]];
    previous = blank;
  }
  if (board.every((v, i) => v === (i + 1) % 9)) [board[7], board[8]] = [board[8], board[7]];
  return { stage, board, steps: 0, scramble };
}

const DIRECTIONS = [
  [-3, 1, 4],
  [1, 2, 8],
  [3, 4, 1],
  [-1, 8, 2],
];
export const rotatePipe = (mask) => ((mask << 1) & 15) | (mask >> 3);
/** Generate a solvable network and its private construction witness. Straight
 * pipes have two distinct orientations; other pieces have four. The increasing
 * scramble budget counts real clockwise moves back to this witness, rather than
 * decorative round labels. Alternatives remain valid solutions. */
export function createPipePuzzle(state, stage = 1) {
  const route = routedCells(state, 3);
  const solution = Array(9).fill(0);
  for (let i = 1; i < route.length; i++) {
    const from = route[i - 1],
      to = route[i];
    const direction = DIRECTIONS.find(([delta]) => from + delta === to);
    solution[from] |= direction[1];
    solution[to] |= direction[2];
  }
  const periods = solution.map((mask) => (mask === 5 || mask === 10 ? 2 : 4));
  const distances = Array(9).fill(0);
  const scramble = [6, 12, 18][stage - 1];
  for (let turn = 0; turn < scramble; turn++) {
    const available = distances
      .map((distance, i) => (distance < periods[i] - 1 ? i : -1))
      .filter((i) => i >= 0);
    distances[available[random(state, available.length)]]++;
  }
  const board = solution.map((mask, i) => {
    for (let turn = (periods[i] - distances[i]) % periods[i]; turn > 0; turn--)
      mask = rotatePipe(mask);
    return mask;
  });
  return {
    data: { stage, board, source: route[0], destination: route.at(-1), steps: 0, scramble },
    solution,
  };
}
function pipeRound(state, stage = 1) {
  return createPipePuzzle(state, stage).data;
}

/** Source lighting follows reciprocal in-bounds ports. Completion requires every
 * pipe, both endpoints and no leaks, allowing valid alternatives to generated art. */
export function pipeNetwork(data) {
  const lit = new Set([data.source]),
    queue = [data.source];
  let leaks = 0;
  for (let head = 0; head < queue.length; head++) {
    const i = queue[head];
    for (const [delta, port, opposite] of DIRECTIONS) {
      if (!(data.board[i] & port)) continue;
      const next = i + delta;
      if (next < 0 || next >= 9 || !adjacent(i, next, 3) || !(data.board[next] & opposite)) {
        leaks++;
        continue;
      }
      if (!lit.has(next)) {
        lit.add(next);
        queue.push(next);
      }
    }
  }
  return {
    lit: [...lit],
    leaks,
    solved: lit.size === 9 && leaks === 0 && lit.has(data.destination),
  };
}

function numberRound(state, stage = 1) {
  const count = [9, 12, 16][stage - 1];
  return {
    stage,
    board: shuffled(
      state,
      Array.from({ length: count }, (_, i) => i + 1),
    ),
    next: 1,
    combo: 0,
    misses: 0,
  };
}
function trinketRound(state, stage = 1, combo = 0) {
  const count = stage <= 3 ? 9 : stage <= 6 ? 12 : 16,
    pair = random(state, 30);
  const others = shuffled(
    state,
    Array.from({ length: 30 }, (_, i) => i).filter((v) => v !== pair),
  ).slice(0, count - 2);
  return { stage, board: shuffled(state, [pair, pair, ...others]), selected: -1, combo, misses: 0 };
}

/** Initialize finite scored puzzles. Answer routes are generation-only and are not
 * included in derived views; the server independently replays the same seed. */
export function createPuzzle(state) {
  switch (state.gameId) {
    case 'colour-path':
      state.data = ribbonRound(state);
      break;
    case 'memory-pairs':
      state.data = memoryRound(state);
      break;
    case 'tile-slide':
      state.data = slideRound(state);
      break;
    case 'rotation-loop':
      state.data = pipeRound(state);
      break;
    case 'number-trail':
      state.data = numberRound(state);
      break;
    case 'spot-pair':
      state.data = trinketRound(state);
      break;
    default:
      reject('Unknown puzzle.');
  }
}

function advance(state, total, points) {
  const stage = state.data.stage;
  const events = state.data.events || [];
  state.score += points;
  if (stage >= total) {
    state.completed = true;
    state.data.events = [...events, { type: 'complete', points, stage }];
    return;
  }
  // Hold the solved layout and RNG until a canonical Next level intent. UI
  // timing may request that intent, but never manufactures a board or points.
  state.data.stageComplete = true;
  state.data.events = [...events, { type: 'stage-complete', points, stage, nextStage: stage + 1 }];
}

/** A selected endpoint owns its line. Backtracking and redrawing are legal; no
 * partial line earns points, and all complete pairs must cover the board. */
function traceRibbon(state, move) {
  const d = state.data;
  if (move.type === 'option') {
    if (d.active >= 0) d.paths[d.active] = [];
    d.active = -1;
    return;
  }
  const index = move.index,
    endpoint = d.pairs.findIndex((pair) => pair.includes(index));
  if (d.active < 0) {
    if (endpoint < 0) reject('Start at a labelled endpoint.');
    d.active = endpoint;
    d.paths[endpoint] = [index];
    d.steps++;
    return;
  }
  const path = d.paths[d.active],
    last = path.at(-1),
    previous = path.indexOf(index);
  if (previous >= 0) {
    d.paths[d.active] = path.slice(0, previous + 1);
    d.steps++;
    return;
  }
  if (!adjacent(last, index, 4)) reject('Continue to a neighboring square.');
  if (
    (endpoint >= 0 && endpoint !== d.active) ||
    d.paths.some((p, i) => i !== d.active && p.includes(index))
  )
    reject('Lines cannot cross.');
  path.push(index);
  d.steps++;
  if (endpoint === d.active) {
    d.events = [{ type: 'connect', cells: [...path] }];
    d.active = -1;
    const finished = d.paths.every(
      (p, i) => p.length >= 2 && d.pairs[i].includes(p[0]) && d.pairs[i].includes(p.at(-1)),
    );
    if (finished && new Set(d.paths.flat()).size === 16)
      advance(state, 3, 200 + d.stage * 75 + Math.max(0, 80 - (d.steps - 16) * 5));
  }
}

function memoryPair(state, move) {
  const d = state.data;
  if (move.type === 'option') {
    if (d.opened.length !== 2) reject('No missed pair to hide.');
    d.opened = [];
    return;
  }
  const index = move.index;
  if (d.opened.length >= 2 || d.opened.includes(index) || d.found.includes(index))
    reject('This card is unavailable.');
  d.opened.push(index);
  d.events = [{ type: 'reveal', cells: [index] }];
  if (d.opened.length !== 2) return;
  d.attempts++;
  if (d.board[d.opened[0]] !== d.board[d.opened[1]]) {
    d.combo = 0;
    d.events = [{ type: 'miss', cells: [...d.opened] }];
    return;
  }
  const pair = [...d.opened];
  d.found.push(...pair);
  d.opened = [];
  d.combo++;
  const points = 30 + Math.min(d.combo - 1, 4) * 5;
  state.score += points;
  d.events = [{ type: 'match', cells: pair, points, combo: d.combo }];
  if (d.found.length === d.board.length) advance(state, 3, 100 + d.stage * 75);
}

/** Legal discrete moves earn points only for meaningful transitions. Puzzle
 * completion holds a solved stage until explicit advancement; misses break combos without negative scores. */
export function movePuzzle(state, move) {
  const d = state.data,
    index = move.index;
  if (d.stageComplete) {
    if (move.type !== 'option' || index !== 0) reject('Continue to the next level.');
    const creators = {
      'colour-path': ribbonRound,
      'memory-pairs': memoryRound,
      'tile-slide': slideRound,
      'rotation-loop': pipeRound,
      'number-trail': numberRound,
      'spot-pair': trinketRound,
    };
    const total = state.gameId === 'spot-pair' ? 10 : 3;
    if (!Number.isInteger(d.stage) || d.stage < 1 || d.stage >= total)
      reject('No next level exists.');
    state.data = creators[state.gameId](state, d.stage + 1, d.combo || 0);
    state.data.events = [{ type: 'stage', stage: d.stage + 1, points: 0 }];
    return;
  }
  d.events = [];
  switch (state.gameId) {
    case 'colour-path':
      traceRibbon(state, move);
      break;
    case 'memory-pairs':
      memoryPair(state, move);
      break;
    case 'tile-slide':
      d.steps++;
      if (slide(d.board, index))
        advance(state, 3, 150 + d.stage * 75 + Math.max(0, 60 - (d.steps - d.scramble) * 3));
      else d.events = [{ type: 'slide', cells: [index, d.board.indexOf(0)] }];
      break;
    case 'rotation-loop':
      d.board[index] = rotatePipe(d.board[index]);
      d.steps++;
      if (pipeNetwork(d).solved)
        advance(
          state,
          3,
          150 + d.stage * 75 + Math.max(0, 60 - Math.max(0, d.steps - d.scramble) * 2),
        );
      else d.events = [{ type: 'rotate', cells: [index] }];
      break;
    case 'number-trail': {
      if (d.board[index] !== d.next) {
        d.combo = 0;
        d.misses++;
        d.events = [{ type: 'miss', cells: [index] }];
        break;
      }
      d.combo++;
      d.next++;
      const points = 5 + Math.min(d.combo - 1, 4) * d.stage;
      state.score += points;
      d.events = [{ type: 'find', cells: [index], points, combo: d.combo }];
      if (d.next > d.board.length)
        advance(state, 3, 100 + d.stage * 75 + Math.min(d.combo, 16) * 3);
      break;
    }
    case 'spot-pair':
      if (d.selected < 0) {
        d.selected = index;
        break;
      }
      if (d.selected === index) {
        d.selected = -1;
        break;
      }
      if (d.board[index] === d.board[d.selected]) {
        d.combo++;
        advance(
          state,
          10,
          100 +
            Math.min(d.combo - 1, 4) * 15 +
            (d.stage === 3 ? 75 : d.stage === 6 ? 150 : d.stage === 10 ? 225 : 0),
        );
      } else {
        d.events = [{ type: 'miss', cells: [d.selected, index] }];
        d.selected = -1;
        d.combo = 0;
        d.misses++;
      }
      break;
  }
}
